// Display Banner Ads - Listen Network Tactic
// Handles file uploads and form management for this specific tactic

import { createUppyUploader } from '../shared/custom_podpromote_uppy_uploader.js'

document.addEventListener('alpine:init', () => {
  Alpine.data('display_banner_ads_listen_network_x_data', () => ({
    // Form data
    formData: {
      targeting: {
        genders: [],
        age_ranges: [],
        household_incomes: [],
        locations: [],
        keywords: [],
        audience_interests: []
      },
      episodes: []
    },

    // Input variable for keywords
    newKeyword: '',

    init() {
      // Initialize with one episode row by default
      if (this.formData.episodes.length === 0) {
        this.addEpisodeRow();
      }
      
      // Load saved configuration from backend
      this.loadSavedConfiguration();
      
      // Use $nextTick to ensure DOM is fully rendered
      this.$nextTick(() => {
        this.initializeUppyUploader();
        this.initializeEpisodeSelects();
      });
      
      // Set up cleanup when component is destroyed
      this.$cleanup = () => {
        this.cleanup();
      };
    },

    // Load saved configuration from backend
    loadSavedConfiguration() {
      try {
        const savedDataDiv = this.$refs.savedConfigData;
        if (savedDataDiv && savedDataDiv.dataset.savedConfig) {
          const savedConfig = JSON.parse(savedDataDiv.dataset.savedConfig);
          
          if (savedConfig) {
            // Load targeting data
            if (savedConfig.targeting) {
              if (savedConfig.targeting.genders) {
                this.formData.targeting.genders = savedConfig.targeting.genders;
              }
              if (savedConfig.targeting.age_ranges) {
                this.formData.targeting.age_ranges = savedConfig.targeting.age_ranges;
              }
              if (savedConfig.targeting.household_incomes) {
                this.formData.targeting.household_incomes = savedConfig.targeting.household_incomes;
              }
              if (savedConfig.targeting.locations) {
                this.formData.targeting.locations = savedConfig.targeting.locations;
              }
              if (savedConfig.targeting.keywords) {
                this.formData.targeting.keywords = savedConfig.targeting.keywords;
              }
              if (savedConfig.targeting.audience_interests) {
                this.formData.targeting.audience_interests = savedConfig.targeting.audience_interests;
              }
            }
            
            // Load brand guidelines file data if it exists
            if (savedConfig.brand_guidelines_file && savedConfig.brand_guidelines_file.url && savedConfig.brand_guidelines_file.url !== 'unknown') {
              // Show upload status
              const statusElement = document.getElementById('brand_guidelines_file-upload-status');
              if (statusElement) {
                statusElement.classList.remove('hidden');
              }
              
              // Store in hidden input
              const hiddenInput = document.getElementById('brand_guidelines_file_data');
              if (hiddenInput) {
                hiddenInput.value = JSON.stringify(savedConfig.brand_guidelines_file);
              }
            }
            
            // Load episodes data
            if (savedConfig.episodes && savedConfig.episodes.length > 0) {
              // Clear existing episodes and load saved ones
              this.formData.episodes = [];
              
              savedConfig.episodes.forEach((savedEpisode, index) => {
                if (savedEpisode.episode_id) {
                  const newEpisode = {
                    id: `episode_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${index}`,
                    episode_id: savedEpisode.episode_id,
                    release_date: savedEpisode.release_date || '',
                    apple_link: savedEpisode.apple_link || '',
                    spotify_link: savedEpisode.spotify_link || ''
                  };
                  this.formData.episodes.push(newEpisode);
                }
              });
              
              // Populate episode select dropdowns with saved data
              this.$nextTick(() => {
                setTimeout(() => {
                  this.populateEpisodeSelects();
                }, 100);
              });
            }
          }
        }
      } catch (error) {
        console.error('Error loading saved configuration:', error);
      }
    },

    // Populate episode select dropdowns with saved data
    populateEpisodeSelects() {
      this.formData.episodes.forEach((episode, index) => {
        if (episode.episode_id) {
          const selectElement = document.getElementById(`episode-select-${episode.id}`);
          
          if (selectElement) {
            selectElement.value = episode.episode_id;
          }
        }
      });
    },

    // Pill management functions
    addGender(gender) {
      if (gender && !this.formData.targeting.genders.includes(gender)) {
        this.formData.targeting.genders.push(gender);
        // Reset the select dropdown
        const selectElement = document.querySelector('select[data-gender-select]');
        if (selectElement) selectElement.value = '';
      }
    },

    removeGender(gender) {
      const index = this.formData.targeting.genders.indexOf(gender);
      if (index > -1) {
        this.formData.targeting.genders.splice(index, 1);
      }
    },

    addAgeRange(ageRange) {
      if (ageRange && !this.formData.targeting.age_ranges.includes(ageRange)) {
        this.formData.targeting.age_ranges.push(ageRange);
        // Reset the select dropdown
        const selectElement = document.querySelector('select[data-age-range-select]');
        if (selectElement) selectElement.value = '';
      }
    },

    removeAgeRange(ageRange) {
      const index = this.formData.targeting.age_ranges.indexOf(ageRange);
      if (index > -1) {
        this.formData.targeting.age_ranges.splice(index, 1);
      }
    },

    addHouseholdIncome(income) {
      if (income && !this.formData.targeting.household_incomes.includes(income)) {
        this.formData.targeting.household_incomes.push(income);
        // Reset the select dropdown
        const selectElement = document.querySelector('select[data-household-income-select]');
        if (selectElement) selectElement.value = '';
      }
    },

    removeHouseholdIncome(income) {
      const index = this.formData.targeting.household_incomes.indexOf(income);
      if (index > -1) {
        this.formData.targeting.household_incomes.splice(index, 1);
      }
    },

    addLocation(location) {
      if (location && !this.formData.targeting.locations.includes(location)) {
        this.formData.targeting.locations.push(location);
        // Reset the select dropdown
        const selectElement = document.querySelector('select[data-location-select]');
        if (selectElement) selectElement.value = '';
      }
    },

    removeLocation(location) {
      const index = this.formData.targeting.locations.indexOf(location);
      if (index > -1) {
        this.formData.targeting.locations.splice(index, 1);
      }
    },

    addKeyword() {
      if (this.newKeyword && this.newKeyword.trim()) {
        const keyword = this.formatKeywordText(this.newKeyword.trim());
        if (keyword && !this.formData.targeting.keywords.includes(keyword)) {
          this.formData.targeting.keywords.push(keyword);
        }
        this.newKeyword = '';
      }
    },

    removeKeyword(keyword) {
      const index = this.formData.targeting.keywords.indexOf(keyword);
      if (index > -1) {
        this.formData.targeting.keywords.splice(index, 1);
      }
    },

    addAudienceInterest(interest) {
      if (interest && !this.formData.targeting.audience_interests.includes(interest)) {
        this.formData.targeting.audience_interests.push(interest);
        // Reset the select dropdown
        const selectElement = document.querySelector('select[data-audience-interest-select]');
        if (selectElement) selectElement.value = '';
      }
    },

    removeAudienceInterest(interest) {
      const index = this.formData.targeting.audience_interests.indexOf(interest);
      if (index > -1) {
        this.formData.targeting.audience_interests.splice(index, 1);
      }
    },

    // Format keyword text: only allow letters, numbers, hyphens, and spaces between words
    formatKeywordText(text) {
      return text
        .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except letters, numbers, spaces, and hyphens
        .replace(/^[\s-]+|[\s-]+$/g, '') // Remove leading/trailing spaces and hyphens
        .replace(/[\s-]+/g, ' ') // Replace multiple spaces/hyphens with single space
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim();
    },
    
    initializeUppyUploader() {
      try {
        // Check if Uppy is already initialized to prevent double initialization
        if (window.uppyInstances && window.uppyInstances['display-banner-ads-listen-network-uploader']) {
          return;
        }
        
        // Get show ID, campaign ID, and campaign tactic ID from URL
        const showId = this.getShowId();
        const campaignId = this.getCampaignId();
        const campaignTacticId = this.getCampaignTacticId();
        
        if (!showId) {
          return;
        }
        
        if (!campaignId) {
          return;
        }
        
        if (!campaignTacticId) {
          return;
        }

        let that = this;

        // Initialize Uppy uploader using the modular approach
        // All Uppy logic is outside Alpine, Alpine instance is passed for callbacks
        const uppyInstance = createUppyUploader({
          id: 'display-banner-ads-listen-network-uploader',
          show_id: showId,
          campaign_id: campaignId,
          campaign_tactic_id: campaignTacticId,
          trigger_id: '#brand_guidelines_file-uppy-trigger',
          max_file_size: 100 * 1024 * 1024, // 100MB
          allowed_file_types: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.ms-powerpoint'
          ],
          max_number_of_files: 1,
          auto_proceed: false,
          close_after_finish: true,
          note: 'Accepted files: PDF, DOC, DOCX, PPT, PPTX. Maximum size: 100MB.',
          on_complete: function(result) {
            that.handleUploadComplete(result);
          },
          on_error: function(file, error, response) {
            that.handleUploadError(file, error, response);
          },
          alpine: this // Pass Alpine instance for callbacks
        });
        
        // Store the instance globally to prevent re-initialization
        if (!window.uppyInstances) {
          window.uppyInstances = {};
        }
        window.uppyInstances['display-banner-ads-listen-network-uploader'] = uppyInstance;
        

      } catch (error) {
        // Failed to initialize Uppy uploader
      }
    },
    
    // Cleanup function to dispose of Uppy instance
    cleanup() {
      try {
        if (window.uppyInstances && window.uppyInstances['display-banner-ads-listen-network-uploader']) {
          const uppyInstance = window.uppyInstances['display-banner-ads-listen-network-uploader'];
          
          // Clean up the Uppy instance
          if (uppyInstance && typeof uppyInstance.close === 'function') {
            uppyInstance.close();
          }
          
          // Remove from global registry
          delete window.uppyInstances['display-banner-ads-listen-network-uploader'];
          

        }
      } catch (error) {
        // Error during Uppy cleanup
      }
    },
    
    getShowId() {
      const urlParts = window.location.pathname.split('/');
      const showIndex = urlParts.indexOf('shows');
      if (showIndex !== -1 && showIndex + 1 < urlParts.length) {
        return urlParts[showIndex + 1];
      }
      return null;
    },

    getCampaignId() {
      const urlParts = window.location.pathname.split('/');
      const campaignIndex = urlParts.indexOf('campaigns');
      if (campaignIndex !== -1 && campaignIndex + 1 < urlParts.length) {
        return urlParts[campaignIndex + 1];
      }
      return null;
    },
    
    getCampaignTacticId() {
      // Try to get from the routes div first
      const routesDiv = this.$refs.routes;
      if (routesDiv && routesDiv.dataset.campaignTacticId) {
        return routesDiv.dataset.campaignTacticId;
      }
      
      // Fallback to URL parsing if needed
      const urlParts = window.location.pathname.split('/');
      const tacticIndex = urlParts.indexOf('campaign-tactics');
      if (tacticIndex !== -1 && tacticIndex + 1 < urlParts.length) {
        return urlParts[tacticIndex + 1];
      }
      
      return null;
    },
    
    handleUploadComplete(result) {
      if (result.successful && result.successful.length > 0) {
        const file = result.successful[0];
        
        // Safety check for this context
        if (!this || !this.formData) {
          return;
        }
        
        // Parse the response to get the S3 data
        let responseData;
        try {
          // The response should be the JSON from the S3 upload controller
          if (file.response) {
            responseData = typeof file.response === 'string' ? JSON.parse(file.response) : file.response;
          } else {
            responseData = {};
          }
        } catch (e) {
          console.error('Error parsing response:', e);
          responseData = {};
        }
        
        // Extract S3 data from response - the controller returns key, url, filename
        const s3Key = responseData?.body?.key || 'unknown';
        const s3Url = responseData?.uploadURL || 'unknown';
        const filename = responseData?.filename || file.name;
        
        // Store file data in hidden input
        const hiddenInput = document.getElementById('brand_guidelines_file_data');
        if (hiddenInput) {
          hiddenInput.value = JSON.stringify({
            name: file.name,
            size: file.size,
            type: file.type,
            s3_key: s3Key,
            url: s3Url
          });
        }
        
        // Show the upload status with filename
        this.showUploadSuccess(filename);
      } else if (result.failed && result.failed.length > 0) {
        // Handle failed uploads
        const failedFile = result.failed[0];
        if (failedFile && failedFile.error) {
          this.handleUploadError(failedFile, failedFile.error, failedFile.response);
        }
      }
    },
    
    handleUploadError(file, error, response) {
      // Safety check for this context
      if (!this || !this.formData) {
        return;
      }
      
      // Show error message in UI but don't show alert (let form validation handle it)
      let errorMessage = error.message || 'Unknown error occurred';
      
      if (error.message && error.message.includes('size')) {
        errorMessage = `File Size Error: ${error.message}\n\nPlease upload a file smaller than 100MB.`;
      } else if (error.message && error.message.includes('type')) {
        errorMessage = `File Type Error: ${error.message}\n\nPlease upload a PDF, DOC, DOCX, PPT, or PPTX file.`;
      }
      
      // Don't show alert here - let the form validation handle all error reporting
      this.showUploadError(error.message || 'Upload failed');
    },
    
    showUploadSuccess(filename) {
      // Show the upload status div
      const statusElement = document.getElementById('brand_guidelines_file-upload-status');
      if (statusElement) {
        statusElement.classList.remove('hidden');
      }
      
      // Set the filename in the display
      const filenameElement = document.getElementById('brand_guidelines_file-filename');
      if (filenameElement) {
        filenameElement.textContent = filename;
      }
      
      // Set up remove file button
      this.setupRemoveFileButton();
    },
    
    setupRemoveFileButton() {
      const removeButton = document.getElementById('brand_guidelines_file-remove-file');
      if (removeButton) {
        removeButton.addEventListener('click', () => {
          this.removeUploadedFile();
        });
      }
    },
    
    removeUploadedFile() {
      // Clear the hidden input
      const hiddenInput = document.getElementById('brand_guidelines_file_data');
      if (hiddenInput) {
        hiddenInput.value = '';
      }
      
      // Hide the upload status (which includes the remove button)
      const statusElement = document.getElementById('brand_guidelines_file-upload-status');
      if (statusElement) {
        statusElement.classList.add('hidden');
      }
      
      // Reset the Uppy instance to allow new uploads
      if (window.uppyInstances && window.uppyInstances['display-banner-ads-listen-network-uploader']) {
        const uppyInstance = window.uppyInstances['display-banner-ads-listen-network-uploader'];
        if (uppyInstance && typeof uppyInstance.reset === 'function') {
          uppyInstance.reset();
        }
      }
    },
    
    showUploadError(message) {
      // Show error message to user
      const errorMessage = document.getElementById('upload-error-message');
      if (errorMessage) {
        errorMessage.style.display = 'block';
        errorMessage.textContent = message;
      }
    },
    
        // Episode management
    addEpisodeRow() {
      if (this.formData.episodes.length >= 5) {
        alert('Maximum of 5 episodes allowed');
        return;
      }
      
      const newEpisode = {
        id: Date.now() + Math.random(), // Unique identifier for each episode row
        episode_id: '',
        release_date: '',
        apple_link: '',
        spotify_link: ''
      };
      
      this.formData.episodes.push(newEpisode);
      
      // Initialize event listeners for the new row
      this.$nextTick(() => {
        this.initializeEpisodeSelects();
      });
    },

    removeEpisodeRow(episodeId) {
      // Don't allow removing the last episode row
      if (this.formData.episodes.length <= 1) {
        return;
      }
      
      // Find the index of the episode to remove
      const indexToRemove = this.formData.episodes.findIndex(episode => episode.id === episodeId);
      
      if (indexToRemove !== -1) {
        // Remove the episode from the array
        this.formData.episodes.splice(indexToRemove, 1);
      } else {
        return;
      }
      
      // Reinitialize event listeners after removing a row
      this.$nextTick(() => {
        this.initializeEpisodeSelects();
      });
    },

    handleEpisodeSelection(episodeId, index) {
      if (episodeId && this.formData.episodes[index]) {
        // Check for duplicate episodes (excluding the current index)
        const isDuplicate = this.formData.episodes.some((episode, i) => 
          i !== index && episode.episode_id === episodeId
        );
        
        if (isDuplicate) {
          // Show error message
          alert('This episode has already been selected. Please choose a different episode.');
          
          // Reset the select element to empty
          const selectElement = document.getElementById(`episode-select-${this.formData.episodes[index].id}`);
          if (selectElement) {
            selectElement.value = '';
          }
          
          // Clear the episode data for this row
          this.formData.episodes[index].episode_id = '';
          this.formData.episodes[index].release_date = '';
          this.formData.episodes[index].apple_link = '';
          this.formData.episodes[index].spotify_link = '';
          
          return;
        }
        
        this.formData.episodes[index].episode_id = episodeId;
        
        // Find the selected episode data from the backend episodes
        const selectedEpisode = this.findEpisodeById(episodeId);
        
        if (selectedEpisode) {
          // Handle pub_date
          if (selectedEpisode.pub_date) {
            const formattedDate = this.formatDate(selectedEpisode.pub_date);
            this.formData.episodes[index].release_date = formattedDate;
          } else {
            this.formData.episodes[index].release_date = 'Release date not available';
          }
          
          // Handle distribution platforms (Apple and Spotify links)
          if (selectedEpisode.distribution_platforms) {
            const platforms = selectedEpisode.distribution_platforms;
            
            // Set Apple link using itunes_url key
            if (platforms.itunes_url) {
              this.formData.episodes[index].apple_link = platforms.itunes_url;
            } else {
              this.formData.episodes[index].apple_link = '';
            }
            
            // Set Spotify link using spotify_url key
            if (platforms.spotify_url) {
              this.formData.episodes[index].spotify_link = platforms.spotify_url;
            } else {
              this.formData.episodes[index].spotify_link = '';
            }
          } else {
            this.formData.episodes[index].apple_link = '';
            this.formData.episodes[index].spotify_link = '';
          }
        }
      }
    },

    // Find episode by ID from the backend data
    findEpisodeById(episodeId) {
      // Get episodes data from the global window object
      let episodesData = window.episodesData || [];
      
      if (!episodesData || episodesData.length === 0) {
        return null;
      }
      
      const foundEpisode = episodesData.find(episode => episode.id == episodeId);
      return foundEpisode;
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return 'Date not available';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return 'Date not available';
      }
    },

    // Initialize episode select event listeners
    initializeEpisodeSelects() {
      this.$nextTick(() => {
        this.formData.episodes.forEach((episode) => {
          const selectElement = document.getElementById(`episode-select-${episode.id}`);
          if (selectElement) {
            // Remove existing event listeners to prevent duplicates
            selectElement.removeEventListener('change', this.createEpisodeChangeHandler(episode.id));
            // Add new event listener
            selectElement.addEventListener('change', this.createEpisodeChangeHandler(episode.id));
          }
        });
      });
    },

    // Create a change handler for a specific episode select
    createEpisodeChangeHandler(episodeId) {
      return (event) => {
        const selectedEpisodeId = event.target.value;
        
        // Find the index of the episode in the formData
        const episodeIndex = this.formData.episodes.findIndex(episode => episode.id === episodeId);
        
        if (episodeIndex !== -1) {
          this.handleEpisodeSelection(selectedEpisodeId, episodeIndex);
        }
      };
    },

    // Save form configuration
    saveForm() {
      try {
        // Check if brand guidelines file is uploaded by looking for the success message in DOM
        const uploadStatusElement = document.getElementById('brand_guidelines_file-upload-status');
        const hasFileUploaded = uploadStatusElement && !uploadStatusElement.classList.contains('hidden');
        
        if (!hasFileUploaded) {
          alert('Please upload a brand guidelines file before saving.');
          return;
        }
        
        // Simple validation - just check if required fields exist
        if (this.formData.targeting.genders.length === 0) {
          alert('Please add at least one gender for targeting.');
          return;
        }
        
        if (this.formData.targeting.age_ranges.length === 0) {
          alert('Please add at least one age range for targeting.');
          return;
        }
        
        if (this.formData.targeting.household_incomes.length === 0) {
          alert('Please add at least one household income range for targeting.');
          return;
        }
        
        if (this.formData.targeting.locations.length === 0) {
          alert('Please add at least one location for targeting.');
          return;
        }
        
        if (this.formData.episodes.length === 0 || this.formData.episodes.every(ep => !ep.episode_id)) {
          alert('Please select at least one episode to highlight.');
          return;
        }
        
        // Get file data from hidden input
        const hiddenInput = document.getElementById('brand_guidelines_file_data');
        let brandGuidelinesFile = {};
        if (hiddenInput && hiddenInput.value) {
          try {
            brandGuidelinesFile = JSON.parse(hiddenInput.value);
          } catch (e) {
            console.error('Error parsing file data:', e);
          }
        }
        
        // Prepare data for submission
        const formDataToSubmit = {
          targeting: this.formData.targeting,
          episodes: this.formData.episodes.filter(ep => ep.episode_id).map(ep => ({
            episode_id: ep.episode_id,
            release_date: ep.release_date,
            apple_link: ep.apple_link,
            spotify_link: ep.spotify_link
          })),
          brand_guidelines_file: brandGuidelinesFile
        };
        
        // Get the submit link from the routes template
        const routesTemplate = this.$refs.routes;
        const submitLink = routesTemplate?.dataset?.submitLink;
        
        if (!submitLink) {
          alert('Submit link not found. Please refresh the page and try again.');
          return;
        }
        
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = submitLink;
        
        // Add CSRF token
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        if (csrfToken) {
          const csrfInput = document.createElement('input');
          csrfInput.type = 'hidden';
          csrfInput.name = 'authenticity_token';
          csrfInput.value = csrfToken;
          form.appendChild(csrfInput);
        }
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'PATCH';
        form.appendChild(methodInput);
        
        // Add form data as JSON
        const formDataInput = document.createElement('input');
        formDataInput.type = 'hidden';
        formDataInput.name = 'form_data';
        formDataInput.value = JSON.stringify(formDataToSubmit);
        form.appendChild(formDataInput);
        
        // Add to DOM and submit
        document.body.appendChild(form);
        form.submit();
      } catch (error) {
        alert('Error saving configuration. Please try again.');
      }
    },

    // Save as draft (no validations)
    saveAsDraft() {
        // Get file data from hidden input
        const hiddenInput = document.getElementById('brand_guidelines_file_data');
        let brandGuidelinesFile = {};
        if (hiddenInput && hiddenInput.value) {
          try {
            brandGuidelinesFile = JSON.parse(hiddenInput.value);
          } catch (e) {
            console.error('Error parsing file data:', e);
          }
        }
        
        // Prepare data for submission (same as saveForm but without validations)
        const formDataToSubmit = {
          targeting: this.formData.targeting,
          episodes: this.formData.episodes.filter(ep => ep.episode_id).map(ep => ({
            episode_id: ep.episode_id,
            release_date: ep.release_date,
            apple_link: ep.apple_link,
            spotify_link: ep.spotify_link
          })),
          brand_guidelines_file: brandGuidelinesFile
        };
        
        // Get the save as draft link from the routes template
        const routesTemplate = this.$refs.routes;
        const saveAsDraftLink = routesTemplate?.dataset?.saveAsDraftLink;
        
        // Create a hidden form and submit it
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = saveAsDraftLink;
        form.style.display = 'none';
        
        // Add CSRF token
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
        form.appendChild(csrfInput);
        
        // Add method override for PATCH
        const methodInput = document.createElement('input');
        methodInput.type = 'hidden';
        methodInput.name = '_method';
        methodInput.value = 'patch';
        form.appendChild(methodInput);
        
        // Add form data
        const formDataInput = document.createElement('input');
        formDataInput.type = 'hidden';
        formDataInput.name = 'form_data';
        formDataInput.value = JSON.stringify(formDataToSubmit);
        form.appendChild(formDataInput);
        
        // Add to DOM and submit
        document.body.appendChild(form);
        form.submit();
    },
    
  }));
});
